import React, { useEffect, useState, useRef, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, addDoc, deleteDoc, updateDoc, serverTimestamp, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { updateEmail, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { Dialog, Transition } from '@headlessui/react';
import { constructDisplayName, parseDisplayName, getUserDisplayName, getDisplayNameInitial } from '../utils/nameUtils';
import { deleteProfileImage } from '../utils/imageUpload';
import ImageUpload from '../components/ImageUpload';
import { BIBLE_BOOKS, getVerseText } from '../utils/bibleData';
import { BADGE_DEFS, getBadgeName, getBadgeDescription, calculateDailyStreak, checkAndAwardBadges } from '../utils/badgeSystem';
import { getFamilyCompletionData } from '../utils/familyCompletion';
import {
  MicrophoneIcon, FireIcon, BookOpenIcon, SpeakerWaveIcon, StarIcon,
  UsersIcon, ShieldCheckIcon, RocketLaunchIcon, TrophyIcon, CheckIcon, TrashIcon,
  BellIcon
} from '@heroicons/react/24/outline';

import FamilyInvitations from '../components/FamilyInvitations';
import LetterBox from '../components/LetterBox';
import StudyNotesManager from '../components/StudyNotesManager';
import WaveformTrimmer from '../components/WaveformTrimmer';
import { DEFAULT_AUDIO_PROFILE, VOICE_TYPE_PRESETS, mergeAudioProfile, AudioEnhancementFields } from '../components/AudioEnhancementEditor';

// Collect the current user's uid plus every fellow family member's uid.
async function getFamilyMemberIds(uid) {
  const ids = new Set([uid]);
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    const familyIds = userDoc.data()?.familyIds || [];
    for (const familyId of familyIds) {
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      if (familyDoc.exists()) {
        (familyDoc.data().memberIDs || familyDoc.data().memberIds || []).forEach(id => ids.add(id));
      }
    }
  } catch (e) {
    console.error('Error resolving family member ids:', e);
  }
  return [...ids];
}

// Fetch recordings for a set of user ids (Firestore 'in' is capped at 10).
async function fetchRecordingsForUserIds(userIds) {
  const byId = new Map();
  for (let i = 0; i < userIds.length; i += 10) {
    const batch = userIds.slice(i, i + 10);
    const snap = await getDocs(query(collection(db, 'recordings'), where('userId', 'in', batch)));
    snap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
  }
  return [...byId.values()];
}

// A single tappable row inside a grouped mobile-style menu section.
function MenuRow({ label, onClick, badge, danger, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-3.5 text-left transition-colors ${
        danger ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-400/10' : 'text-gray-800 dark:text-white/90 hover:bg-gray-100 dark:hover:bg-white/10'
      }`}
    >
      <span className="flex items-center gap-3 font-medium">
        {icon}
        {label}
      </span>
      <span className="flex items-center gap-2">
        {badge !== undefined && badge !== null && (
          <span className="text-xs font-semibold text-cerulean bg-cerulean/10 rounded-full px-2 py-0.5 min-w-[1.5rem] text-center">
            {badge}
          </span>
        )}
        {!danger && (
          <svg className="w-4 h-4 text-gray-300 dark:text-white/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </span>
    </button>
  );
}

// A titled group of menu rows rendered as a rounded card.
function MenuSection({ title, children }) {
  return (
    <div className="mb-6">
      {title && (
        <h3 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-white/35">{title}</h3>
      )}
      <div className="bg-gray-50 dark:bg-white/5 rounded-[3px] overflow-hidden divide-y divide-gray-100 dark:divide-white/10">
        {children}
      </div>
    </div>
  );
}

// A settings group: icon-chip header + description, with controls below.
function SettingsSection({ icon, title, description, children }) {
  return (
    <div className="rounded-[3px] border border-gray-200 dark:border-white/10 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-[3px] bg-cerulean/10 flex items-center justify-center mt-0.5 text-cerulean">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
          {description && <p className="text-xs text-gray-500 dark:text-white/40 leading-snug mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function ProfilePage() {
  const { currentUser, logout, refreshUserData } = useAuth();
  const { translation, translations, changeTranslation } = useTranslation();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [recordingsDrawerOpen, setRecordingsDrawerOpen] = useState(false);
  const [recordingsView, setRecordingsView] = useState('mine'); // 'mine' | 'family'
  const [myRecordingsTotal, setMyRecordingsTotal] = useState(0);
  const [familyRecordingsTotal, setFamilyRecordingsTotal] = useState(0);
  const [studyNotesDrawerOpen, setStudyNotesDrawerOpen] = useState(false);
  const [familyInvitationsOpen, setFamilyInvitationsOpen] = useState(false);
  const [manageFamilyOpen, setManageFamilyOpen] = useState(false);
  const [manageFamilyList, setManageFamilyList] = useState([]);
  const [manageFamilyLoading, setManageFamilyLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState(null);

  // Inline trim/edit state for the recordings drawer
  const [expandedTrimId, setExpandedTrimId] = useState(null);
  const [trimSavingId, setTrimSavingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  // Inline re-record state for the recordings drawer
  const [expandedReRecordId, setExpandedReRecordId] = useState(null); // card expanded, not yet recording
  const [reRecordingId, setReRecordingId] = useState(null);
  const [reRecordTime, setReRecordTime] = useState(0);
  const [reRecordProcessing, setReRecordProcessing] = useState(null);
  const reRecordMediaRecorderRef = useRef(null);
  const reRecordStreamRef = useRef(null);
  const reRecordIntervalRef = useRef(null);
  const reRecordChunksRef = useRef([]);
  const [userRecordings, setUserRecordings] = useState([]);
  const [allRecordings, setAllRecordings] = useState([]);
  const [bookFilter, setBookFilter] = useState('');
  const [chapterFilter, setChapterFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecordings, setTotalRecordings] = useState(0);
  const pageSize = 20;

  // Drawer form state
  const [profileImage, setProfileImage] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [family, setFamily] = useState('');
  const [familyRelation, setFamilyRelation] = useState('');
  const [birthday, setBirthday] = useState('');
  const [bio, setBio] = useState('');
  const [familyOptions, setFamilyOptions] = useState([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [profileImagePath, setProfileImagePath] = useState('');
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);
  // Which settings page the drawer shows: 'recording' | 'listening' | 'notifications' | 'appearance' | 'audioEnhancement'
  const [settingsView, setSettingsView] = useState('recording');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [newRecordingsNotification, setNewRecordingsNotification] = useState(true);
  const [studyNotesNotification, setStudyNotesNotification] = useState(true);
  const [listeningActivityNotification, setListeningActivityNotification] = useState(true);
  const [notificationFrequency, setNotificationFrequency] = useState('daily');
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderDays, setReminderDays] = useState(['sat']);
  const [reminderHour, setReminderHour] = useState(11);
  const [reminderMinute, setReminderMinute] = useState(0);
  const [autoEnhanceRecordings, setAutoEnhanceRecordings] = useState(false);
  const [audioEnhancementProfile, setAudioEnhancementProfile] = useState(DEFAULT_AUDIO_PROFILE);
  const [showCassette, setShowCassette] = useState(true);
  const [cassetteCompact, setCassetteCompact] = useState(false);
  // Default record point (no active plan): 'last-recorded' = continue from the
  // chapter you most recently recorded; 'whole-bible' = next gap in canonical order.
  const [defaultRecordPoint, setDefaultRecordPoint] = useState('last-recorded');
  // How many verses per text block on the Record page (line break after every Nth verse)
  const [recordVersesPerBlock, setRecordVersesPerBlock] = useState(3);
  // Default listen point (no active plan): 'last-listened' = resume where you were;
  // 'whole-bible' = advance to the next chapter in canonical order.
  const [defaultListenPoint, setDefaultListenPoint] = useState('last-listened');
  // Global, client-side (Web Audio API) voice enhancement applied during
  // Listen playback. Defaults to on; original recordings are never touched.
  const [enhanceAudio, setEnhanceAudio] = useState(true);
  // How many verses per text block on the Listen page (line break after every Nth verse)
  const [listenVersesPerBlock, setListenVersesPerBlock] = useState(3);

  // Bible Plans state
  const [biblePlansDrawerOpen, setBiblePlansDrawerOpen] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [recordingPlans, setRecordingPlans] = useState([]);
  const [listeningPlans, setListeningPlans] = useState([]);
  const [completedPlans, setCompletedPlans] = useState([]);
  const [selectedRecordingPlan, setSelectedRecordingPlan] = useState('');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [planSearch, setPlanSearch] = useState('');
  const [planFamilyCompletionData, setPlanFamilyCompletionData] = useState(null);
  
  // Letter Box state
  const [letterBoxOpen, setLetterBoxOpen] = useState(false);
  const [letterBoxTab, setLetterBoxTab] = useState('toFamily');
  // Whether the user is admin/creator of any of their families (shows the All Family Letters row)
  const [isAnyFamilyAdmin, setIsAnyFamilyAdmin] = useState(false);

  // Email change modal
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [newEmailValue, setNewEmailValue] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [needsReauth, setNeedsReauth] = useState(false);
  const [emailChanging, setEmailChanging] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // Badges state
  const [badgesDrawerOpen, setBadgesDrawerOpen] = useState(false);
  const [userBadges, setUserBadges] = useState([]);
  const [userStreaks, setUserStreaks] = useState({});
  const [isLoadingBadges, setIsLoadingBadges] = useState(false);

  useEffect(() => {
    if (currentUser) {
      getDoc(doc(db, 'users', currentUser.uid)).then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(data);
          setEmail(data.email || '');
          setPhone(data.phone || '');
          setFamily(Array.isArray(data.familyIds) ? data.familyIds.join(', ') : '');
          setFamilyRelation(data.familyRelation || '');
          let birthdayValue = '';
          if (data.birthday) {
            const dateObj = new Date(data.birthday);
            if (!isNaN(dateObj.getTime())) {
              birthdayValue = dateObj.toISOString().split('T')[0];
            }
          }
          setBirthday(birthdayValue);
          setBio(data.bio || '');
          setProfileImageUrl(data.profileImageUrl || '');
          setProfileImagePath(data.profileImagePath || '');
          
          // Load notification preferences
          const notificationPrefs = data.notificationPreferences || {};
          setEmailEnabled(notificationPrefs.emailEnabled !== false);
          setPushEnabled(notificationPrefs.pushEnabled !== false);
          setNewRecordingsNotification(notificationPrefs.newRecordings !== false); // Default to true if not set
          setStudyNotesNotification(notificationPrefs.studyNotes !== false); // Default to true if not set
          setListeningActivityNotification(notificationPrefs.listeningActivity !== false); // Default to true if not set
          setNotificationFrequency(notificationPrefs.frequency || 'daily'); // Default to daily if not set
          const rr = notificationPrefs.recordingReminder || {};
          setReminderEnabled(rr.enabled ?? false);
          setReminderDays(rr.days ?? ['sat']);
          setReminderHour(rr.hour ?? 11);
          setReminderMinute(rr.minute ?? 0);
          setAutoEnhanceRecordings(!!(data.recordingPreferences?.autoEnhanceRecordings)); // Default off
          setAudioEnhancementProfile(mergeAudioProfile(data.recordingPreferences?.audioEnhancementProfile));
          setShowCassette(data.recordingPreferences?.showCassette !== false); // Default on
          setCassetteCompact(!!(data.recordingPreferences?.cassetteCompact)); // Default off (full cassette)
          setDefaultRecordPoint(data.defaultRecordPoint || 'last-recorded'); // Default to continuing from last recorded chapter
          setRecordVersesPerBlock(Number(data.recordVersesPerBlock) || 3);
          setDefaultListenPoint(data.defaultListenPoint || 'last-listened'); // Default to resuming where you last listened
          setListenVersesPerBlock(Number(data.listenVersesPerBlock) || 3);
          setEnhanceAudio(data.playbackPreferences?.enhanceAudio !== false); // Default to on
          
          // Handle name fields - if we have separate fields, use them; otherwise parse displayName
          if (data.firstName || data.middleName || data.lastName) {
            setFirstName(data.firstName || '');
            setMiddleName(data.middleName || '');
            setLastName(data.lastName || '');
            // Use existing displayName if it exists, otherwise construct from separate fields
            setDisplayName(data.displayName || constructDisplayName(data.firstName, data.middleName, data.lastName));
            setDisplayNameManuallyEdited(!!data.displayName); // Set to true if displayName was manually set
          } else {
            // Legacy: parse existing displayName into separate fields
            const parsed = parseDisplayName(data.displayName || '');
            setFirstName(parsed.firstName);
            setMiddleName(parsed.middleName);
            setLastName(parsed.lastName);
            setDisplayName(data.displayName || '');
            setDisplayNameManuallyEdited(!!data.displayName); // Set to true if displayName was manually set
          }
        }
      });
    }
  }, [currentUser]);

  // Handle URL parameters to open specific drawers
  useEffect(() => {
    const openPlans = searchParams.get('openPlans');
    if (openPlans === 'true') {
      setBiblePlansDrawerOpen(true);
      // Clean up the URL parameter after opening the drawer
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('openPlans');
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);

  // Fetch family names for dropdown
  useEffect(() => {
    async function fetchFamilies() {
      if (userData && Array.isArray(userData.familyIds) && userData.familyIds.length > 0) {
        const familiesSnap = await getDocs(collection(db, 'families'));
        const options = [];
        let anyAdmin = false;
        familiesSnap.forEach(docSnap => {
          if (userData.familyIds.includes(docSnap.id)) {
            options.push({ id: docSnap.id, name: docSnap.data().name });
            const fam = docSnap.data();
            if (fam.createdBy === currentUser?.uid || (fam.adminIDs || []).includes(currentUser?.uid)) {
              anyAdmin = true;
            }
          }
        });
        setIsAnyFamilyAdmin(anyAdmin);
        setFamilyOptions(options);
        if (options.length > 0) setSelectedFamilyId(options[0].id);
      }
    }
    fetchFamilies();
  }, [userData]);

  // Tally counts for the menu (independent of which view the drawer shows)
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    (async () => {
      try {
        const mineSnap = await getDocs(
          query(collection(db, 'recordings'), where('userId', '==', currentUser.uid))
        );
        if (!cancelled) setMyRecordingsTotal(mineSnap.size);

        const memberIds = await getFamilyMemberIds(currentUser.uid);
        const family = await fetchRecordingsForUserIds(memberIds);
        if (!cancelled) setFamilyRecordingsTotal(family.length);
      } catch (e) {
        console.error('Error counting recordings:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Fetch recordings (mine or family) with filter and pagination
  useEffect(() => {
    async function fetchRecordings() {
      if (!currentUser) {
        return;
      }

      try {
        // 'family' view shows every family member's recordings; 'mine' shows only the user's.
        let all;
        if (recordingsView === 'family') {
          const memberIds = await getFamilyMemberIds(currentUser.uid);
          all = await fetchRecordingsForUserIds(memberIds);
        } else {
          const recordingsSnapshot = await getDocs(
            query(collection(db, 'recordings'), where('userId', '==', currentUser.uid))
          );
          all = recordingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }

        // Store all recordings for search/filtering, and the active-view total
        setAllRecordings(all);
        setTotalRecordings(all.length);

        // Apply filters
        if (bookFilter) {
          all = all.filter(r => r.book === bookFilter);
        }

        if (bookFilter && chapterFilter) {
          all = all.filter(r => String(r.chapter) === String(chapterFilter));
        }

        if (searchTerm) {
          all = all.filter(r => 
            r.passage?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.book?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.chapter?.toString().includes(searchTerm) ||
            r.verse?.toString().includes(searchTerm)
          );
        }
        
        // Sort by creation date (newest first)
        all.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
          const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
          return dateB - dateA;
        });
        
        const totalPages = Math.ceil(all.length / pageSize);
        const startIndex = (page - 1) * pageSize;
        const endIndex = page * pageSize;
        const pageRecordings = all.slice(startIndex, endIndex);
        
        console.log('🔍 ProfilePage - Pagination:', {
          totalRecordings: all.length,
          page,
          pageSize,
          totalPages,
          startIndex,
          endIndex,
          pageRecordingsCount: pageRecordings.length
        });
        
        setTotalPages(totalPages);
        setUserRecordings(pageRecordings);
      } catch (error) {
        console.error('Error fetching recordings:', error);
      }
    }
    fetchRecordings();
  }, [currentUser, recordingsView, bookFilter, chapterFilter, searchTerm, page]);

  // Fetch Bible Plans data
  useEffect(() => {
    if (currentUser) {
      fetchBiblePlansData();
      fetchBadgesData();
    }
  }, [currentUser, userData]);

  const openManageFamily = async () => {
    console.log('openManageFamily called, setting open=true');
    setManageFamilyOpen(true);
    if (manageFamilyList.length > 0) return; // already loaded
    setManageFamilyLoading(true);
    try {
      const familyIds = currentUser?.familyIds || [];
      const docs = await Promise.all(
        familyIds.map(id => getDoc(doc(db, 'families', id)).catch(() => null))
      );
      const families = docs
        .filter(d => d && d.exists())
        .map(d => {
          const data = d.data();
          const isAdmin = (data.adminIDs || []).includes(currentUser.uid) || data.creatorId === currentUser.uid;
          return { id: d.id, ...data, isAdmin };
        });
      setManageFamilyList(families);
    } catch (e) {
      console.error('openManageFamily:', e);
    } finally {
      setManageFamilyLoading(false);
    }
  };

  const openEmailModal = () => {
    setNewEmailValue('');
    setEmailPassword('');
    setNeedsReauth(false);
    setEmailError('');
    setEmailSuccess('');
    setEmailModalOpen(true);
  };

  const doEmailChange = async (password) => {
    setEmailChanging(true);
    setEmailError('');
    try {
      if (password) {
        const credential = EmailAuthProvider.credential(currentUser.email, password);
        await reauthenticateWithCredential(auth.currentUser, credential);
      }
      await updateEmail(auth.currentUser, newEmailValue.trim());
      await updateDoc(doc(db, 'users', currentUser.uid), { email: newEmailValue.trim() });
      setEmail(newEmailValue.trim());
      setEmailSuccess(`Email updated to ${newEmailValue.trim()}`);
      setEmailModalOpen(false);
    } catch (e) {
      if (e?.code === 'auth/requires-recent-login') {
        setNeedsReauth(true);
        setEmailError('Please enter your password to confirm this change.');
      } else if (e?.code === 'auth/wrong-password' || e?.code === 'auth/invalid-credential') {
        setEmailError('Incorrect password. Please try again.');
      } else if (e?.code === 'auth/email-already-in-use') {
        setEmailError('That email is already linked to another account.');
      } else {
        setEmailError(e?.message || 'Could not update email. Please try again.');
      }
    } finally {
      setEmailChanging(false);
    }
  };

  const handleEmailConfirm = (e) => {
    e.preventDefault();
    const trimmed = newEmailValue.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (trimmed === currentUser?.email) {
      setEmailError('That is already your current email address.');
      return;
    }
    if (needsReauth && !emailPassword) {
      setEmailError('Please enter your password.');
      return;
    }
    doEmailChange(needsReauth ? emailPassword : null);
  };

  const handleImageUploadSuccess = async (imageUrl, imageData) => {
    try {

      
      // Delete old profile image if it exists
      if (profileImagePath) {
        await deleteProfileImage(profileImagePath);
      }

      // Update user document with new image URL and path
      const updateData = {
        profileImageUrl: imageUrl,
        profileImagePath: imageData?.path || `users/${currentUser.uid}/profile/profile_${Date.now()}.jpg`
      };
      

      await updateDoc(doc(db, 'users', currentUser.uid), updateData);

      // Update local state
      setProfileImageUrl(imageUrl);
      setProfileImagePath(updateData.profileImagePath);
      setUserData(prev => ({ 
        ...prev, 
        profileImageUrl: imageUrl,
        profileImagePath: updateData.profileImagePath
      }));


    } catch (error) {
      console.error('Error updating profile image:', error);

    }
  };

  const handleDeleteRecording = async (recording) => {
    setRecordingToDelete(recording);
    setDeleteModalOpen(true);
  };

  const confirmDeleteRecording = async () => {
    if (!recordingToDelete) return;
    
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'recordings', recordingToDelete.id));

      // Remove from local state and keep the tallies in sync. A user can only
      // delete their own recording, so both their own total and the family
      // total (which includes them) drop by one.
      setUserRecordings(prev => prev.filter(r => r.id !== recordingToDelete.id));
      setTotalRecordings(t => Math.max(0, t - 1));
      setMyRecordingsTotal(t => Math.max(0, t - 1));
      setFamilyRecordingsTotal(t => Math.max(0, t - 1));

      // Close modal
      setDeleteModalOpen(false);
      setRecordingToDelete(null);
      

    } catch (error) {
      console.error('Error deleting recording:', error);

    }
  };

  const startProfileReRecord = async (recId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      reRecordStreamRef.current = stream;
      const preferTypes = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/mpeg'];
      const mimeType = preferTypes.find(t => MediaRecorder.isTypeSupported(t)) || '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      reRecordChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) reRecordChunksRef.current.push(e.data); };
      reRecordMediaRecorderRef.current = recorder;
      recorder.start();
      setReRecordingId(recId);
      setReRecordTime(0);
      reRecordIntervalRef.current = setInterval(() => setReRecordTime(t => t + 1), 1000);
    } catch (e) {
      alert('Could not access microphone. Please check permissions and try again.');
    }
  };

  const stopProfileReRecord = async () => {
    const recId = reRecordingId;
    if (!recId || !reRecordMediaRecorderRef.current) return;
    clearInterval(reRecordIntervalRef.current);
    setReRecordingId(null);
    setReRecordProcessing(recId);

    await new Promise((resolve) => {
      reRecordMediaRecorderRef.current.onstop = async () => {
        try {
          const blob = new Blob(reRecordChunksRef.current, {
            type: reRecordMediaRecorderRef.current.mimeType || 'audio/webm',
          });
          reRecordStreamRef.current?.getTracks().forEach(t => t.stop());
          reRecordStreamRef.current = null;

          const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
          const { storage } = await import('../config/firebase');
          const ext = blob.type.includes('mp4') || blob.type.includes('m4a') ? 'm4a'
            : blob.type.includes('mpeg') ? 'mp3'
            : 'webm';
          const filename = `re-record-${recId}-${Date.now()}.${ext}`;
          const storageRef = ref(storage, `recordings/${currentUser.uid}/verses/${filename}`);
          await uploadBytes(storageRef, blob, { contentType: blob.type || 'audio/webm' });
          const downloadURL = await getDownloadURL(storageRef);

          await updateDoc(doc(db, 'recordings', recId), {
            audioURL: downloadURL,
            updatedAt: serverTimestamp(),
          });

          setUserRecordings(prev => prev.map(r =>
            r.id === recId ? { ...r, audioURL: downloadURL, audioUrl: downloadURL } : r
          ));
        } catch (e) {
          console.error('Re-record save error:', e);
          alert('Failed to save re-recording. Please try again.');
        } finally {
          setReRecordProcessing(null);
          setReRecordTime(0);
          resolve();
        }
      };
      reRecordMediaRecorderRef.current.stop();
    });
  };

  const handleRestore = async (rec) => {
    if (!window.confirm(`Restore the original recording for ${rec.passage}? This will undo any trimming.`)) return;
    setRestoringId(rec.id);
    try {
      const reqRef = await addDoc(collection(db, 'restoreRequests'), {
        recordingId: rec.id,
        requestedBy: currentUser.uid,
        status: 'pending',
      });
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => { unsub(); reject(new Error('Timed out')); }, 180000);
        const unsub = onSnapshot(reqRef, (snap) => {
          const status = snap.data()?.status;
          if (status === 'done') { clearTimeout(timeout); unsub(); resolve(); }
          else if (status === 'error') { clearTimeout(timeout); unsub(); reject(new Error(snap.data()?.error || 'Restore failed')); }
        });
      });
      const recDoc = await getDoc(doc(db, 'recordings', rec.id));
      if (recDoc.exists()) {
        setUserRecordings(prev => prev.map(r => r.id === rec.id ? { id: recDoc.id, ...recDoc.data() } : r));
      }
    } catch (e) {
      alert(`Failed to restore: ${e.message}`);
    } finally {
      setRestoringId(null);
    }
  };

  const handleApplyTrim = async (rec, trimStart, trimEnd) => {
    setTrimSavingId(rec.id);
    try {
      const reqRef = await addDoc(collection(db, 'trimRequests'), {
        recordingId: rec.id,
        requestedBy: currentUser.uid,
        trimStart,
        trimEnd,
        status: 'pending',
      });

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          unsub();
          reject(new Error('Timed out after 2 minutes'));
        }, 120000);
        const unsub = onSnapshot(reqRef, (snap) => {
          const status = snap.data()?.status;
          if (status === 'done') { clearTimeout(timeout); unsub(); resolve(); }
          else if (status === 'error') { clearTimeout(timeout); unsub(); reject(new Error(snap.data()?.error || 'Trim failed')); }
        });
      });

      const recDoc = await getDoc(doc(db, 'recordings', rec.id));
      if (recDoc.exists()) {
        setUserRecordings(prev => prev.map(r => r.id === rec.id ? { id: recDoc.id, ...recDoc.data() } : r));
      }
      setExpandedTrimId(null);
    } catch (e) {
      alert(`Failed to trim: ${e.message}`);
    } finally {
      setTrimSavingId(null);
    }
  };

  // Bible Plans functions
  const fetchBiblePlansData = async () => {
    if (!currentUser) return;
    
    setIsLoadingPlans(true);
    try {
      // Fetch all available plans
      const plansSnapshot = await getDocs(collection(db, 'plans'));
      const allPlans = plansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailablePlans(allPlans);

      // Fetch user's recording plans
      const recordingPlansSnapshot = await getDocs(
        collection(db, `users/${currentUser.uid}/recordingPlans`)
      );
      const userRecordingPlans = recordingPlansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecordingPlans(userRecordingPlans);

      // Set current recording plan (user can only have one active)
      const activePlan = userRecordingPlans.find(p => p.status === 'active') || userRecordingPlans[0];
      if (activePlan) {
        setSelectedRecordingPlan(activePlan.planId);
      } else {
        setSelectedRecordingPlan('');
      }

      // Fetch user's listening plans
      const listeningPlansSnapshot = await getDocs(
        collection(db, `users/${currentUser.uid}/listeningPlans`)
      );
      let userListeningPlans = listeningPlansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Enforce a single listening plan: collapse any duplicates/extras left over
      // from the old multi-subscribe behavior, keeping the most recent one.
      if (userListeningPlans.length > 1) {
        const millis = (t) => (t?.toMillis ? t.toMillis() : (t ? new Date(t).getTime() : 0));
        const sorted = [...userListeningPlans].sort((a, b) => millis(b.subscribedAt) - millis(a.subscribedAt));
        const keep = sorted[0];
        for (const lp of sorted.slice(1)) {
          try {
            await deleteDoc(doc(db, `users/${currentUser.uid}/listeningPlans`, lp.id));
          } catch (e) {
            console.warn('Could not remove duplicate listening plan', lp.id, e);
          }
        }
        userListeningPlans = [keep];
      }
      setListeningPlans(userListeningPlans);

      // Fetch family's completed plans - Get user data first if not available
      let familyCompletedPlans = [];
      let userFamilyIds = [];
      
      if (userData && userData.familyIds && userData.familyIds.length > 0) {
        userFamilyIds = userData.familyIds;
      } else {
        // Fetch user data if not available
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userDataFromDb = userDoc.data();
          userFamilyIds = userDataFromDb.familyIds || [];
        }
      }
      
      console.log('🔍 ProfilePage - User family IDs:', userFamilyIds);
      
      if (userFamilyIds && userFamilyIds.length > 0) {
        // Get completed plans from all user's families
        for (const familyId of userFamilyIds) {
          console.log(`🔍 ProfilePage - Fetching completed plans for family: ${familyId}`);
          const familyCompletedPlansSnapshot = await getDocs(
            collection(db, `families/${familyId}/completedPlans`)
          );
          const familyPlans = familyCompletedPlansSnapshot.docs.map(doc => ({
            id: doc.id,
            familyId: familyId,
            ...doc.data()
          }));
          console.log(`🔍 ProfilePage - Found ${familyPlans.length} completed plans for family ${familyId}:`, familyPlans);
          familyCompletedPlans.push(...familyPlans);
        }
      }
      
      // Fetch and merge chapter completion data across all families
      const chapterMap = new Map();
      for (const fid of userFamilyIds) {
        const data = await getFamilyCompletionData(fid, db);
        if (data && data.chapters) {
          for (const ch of data.chapters) {
            const key = `${ch.book}_${ch.chapter}`;
            if (!chapterMap.has(key) || ch.completed) chapterMap.set(key, ch);
          }
        }
      }
      setPlanFamilyCompletionData(chapterMap.size > 0 ? { chapters: Array.from(chapterMap.values()) } : null);

      console.log('🔍 ProfilePage - Total completed plans found:', familyCompletedPlans.length);
      // Dedupe by plan so the same completed plan never shows twice, even if the
      // family has more than one completedPlans record for it.
      const dedupedCompletedPlans = Array.from(
        familyCompletedPlans.reduce((map, p) => {
          const key = p.planId || p.id;
          if (!map.has(key)) map.set(key, p);
          return map;
        }, new Map()).values()
      );
      setCompletedPlans(dedupedCompletedPlans);

    } catch (error) {
      console.error('Error fetching Bible plans data:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const updateAudioProfileSection = (section, patch) => {
    setAudioEnhancementProfile((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));
  };

  const updateAudioProfileEqBand = (index, patch) => {
    setAudioEnhancementProfile((prev) => ({
      ...prev,
      equalizer: {
        ...prev.equalizer,
        bands: prev.equalizer.bands.map((b, i) => (i === index ? { ...b, ...patch } : b)),
      },
    }));
  };

  const applyAudioProfileVoicePreset = (voiceType) => {
    const gains = VOICE_TYPE_PRESETS[voiceType] || VOICE_TYPE_PRESETS.off;
    setAudioEnhancementProfile((prev) => ({
      ...prev,
      equalizer: {
        enabled: voiceType !== 'off',
        bands: prev.equalizer.bands.map((b, i) => ({ ...b, gain: gains[i] })),
      },
    }));
  };

  const handleRecordingPlanChange = async (planId) => {
    if (!currentUser) return;
    
    setIsLoadingPlans(true);
    try {
      // Remove existing recording plan subscription
      if (selectedRecordingPlan) {
        const existingSubscription = recordingPlans.find(rp => rp.planId === selectedRecordingPlan);
        if (existingSubscription) {
          const existingPlanRef = doc(db, `users/${currentUser.uid}/recordingPlans`, existingSubscription.id);
          await deleteDoc(existingPlanRef);
        }
      }

      // Add new recording plan subscription (if not "no plan")
      if (planId && planId !== '') {
        const selectedPlan = availablePlans.find(plan => plan.id === planId);
        if (selectedPlan) {
          const newSubscription = {
            planId: planId,
            subscribedAt: serverTimestamp(),
            completedChapters: [],
            currentChapterIndex: 0,
            totalChapters: selectedPlan.chapters?.length || 0,
            lastRecordedAt: null,
            status: 'active',
            chapterProgress: {}
          };
          
          await addDoc(
            collection(db, `users/${currentUser.uid}/recordingPlans`),
            newSubscription
          );
        }
      }

      setSelectedRecordingPlan(planId);
      await fetchBiblePlansData(); // Refresh data
      
    } catch (error) {
      console.error('Error updating recording plan:', error);

    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handleListeningPlanSubscribe = async (planId) => {
    if (!currentUser) return;
    
    setIsLoadingPlans(true);
    try {
      // Already subscribed to this plan? (compare planId, not the doc id)
      const existingSubscription = listeningPlans.find(plan => plan.planId === planId);
      if (existingSubscription) {
        return;
      }

      // Only one listening plan at a time — replace any existing subscription(s).
      for (const lp of listeningPlans) {
        try {
          await deleteDoc(doc(db, `users/${currentUser.uid}/listeningPlans`, lp.id));
          if (lp.planId) {
            await updateFamilyCompletedPlanSubscription(lp.planId, currentUser.uid, 'unsubscribe');
          }
        } catch (e) {
          console.warn('Could not remove prior listening plan', lp.id, e);
        }
      }

      // Add the new listening plan subscription
      const selectedPlan = availablePlans.find(plan => plan.id === planId);
      const newSubscription = {
        planId: planId,
        subscribedAt: serverTimestamp(),
        listenedChapters: [],
        currentChapterIndex: 0,
        totalChapters: selectedPlan?.chapters?.length || 0,
        lastListenedAt: null,
        status: 'active'
      };

      await addDoc(
        collection(db, `users/${currentUser.uid}/listeningPlans`),
        newSubscription
      );

      // Update family completed plans to add user to currentlySubscribed
      await updateFamilyCompletedPlanSubscription(planId, currentUser.uid, 'subscribe');

      await fetchBiblePlansData(); // Refresh data
    } catch (error) {
      console.error('Error subscribing to listening plan:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handleListeningPlanUnsubscribe = async (planId) => {
    if (!currentUser) return;

    setIsLoadingPlans(true);
    try {
      // Resolve the real subscription doc id(s) for this plan and delete them.
      const subs = listeningPlans.filter(lp => lp.planId === planId || lp.id === planId);
      for (const lp of subs) {
        await deleteDoc(doc(db, `users/${currentUser.uid}/listeningPlans`, lp.id));
      }

      // Update family completed plans to move user from currentlySubscribed to formerlySubscribed
      await updateFamilyCompletedPlanSubscription(planId, currentUser.uid, 'unsubscribe');

      await fetchBiblePlansData(); // Refresh data
    } catch (error) {
      console.error('Error unsubscribing from listening plan:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handleListeningToggle = async (planId, action) => {
    if (!currentUser) return;
    
    setIsLoadingPlans(true);
    try {
      // Update family completed plans listening status
      await updateFamilyCompletedPlanListening(planId, currentUser.uid, action);
      
      // Update user's listening plan status
      const listeningPlanRef = doc(db, `users/${currentUser.uid}/listeningPlans`, planId);
      const listeningPlanDoc = await getDoc(listeningPlanRef);
      
      if (listeningPlanDoc.exists()) {
        const updateData = {
          isCurrentlyListening: action === 'start_listening',
          lastListeningActivity: serverTimestamp()
        };
        
        if (action === 'start_listening') {
          updateData.startedListeningAt = serverTimestamp();
        } else if (action === 'stop_listening') {
          updateData.stoppedListeningAt = serverTimestamp();
        }
        
        await updateDoc(listeningPlanRef, updateData);
      }
      
      await fetchBiblePlansData(); // Refresh data
    } catch (error) {
      console.error('Error toggling listening status:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  // Helper function to update family completed plan listening status
  const updateFamilyCompletedPlanListening = async (planId, userId, action) => {
    try {
      if (!userData || !userData.familyIds || userData.familyIds.length === 0) {
        console.log('No family IDs found for user');
        return;
      }

      // Update listening status for each family the user belongs to
      for (const familyId of userData.familyIds) {
        // Find the completed plan document for this family
        const familyCompletedPlansSnapshot = await getDocs(
          collection(db, `families/${familyId}/completedPlans`)
        );
        
        const completedPlanDoc = familyCompletedPlansSnapshot.docs.find(
          doc => doc.data().planId === planId
        );
        
        if (completedPlanDoc) {
          const planData = completedPlanDoc.data();
          const currentlyListening = planData.currentlyListening || [];
          const formerlyListening = planData.formerlyListening || [];
          
          let updatedCurrentlyListening = [...currentlyListening];
          let updatedFormerlyListening = [...formerlyListening];
          
          if (action === 'start_listening') {
            // Add to currentlyListening if not already there
            if (!updatedCurrentlyListening.includes(userId)) {
              updatedCurrentlyListening.push(userId);
            }
            // Remove from formerlyListening if they were there
            updatedFormerlyListening = updatedFormerlyListening.filter(id => id !== userId);
          } else if (action === 'stop_listening') {
            // Remove from currentlyListening
            updatedCurrentlyListening = updatedCurrentlyListening.filter(id => id !== userId);
            // Add to formerlyListening if not already there
            if (!updatedFormerlyListening.includes(userId)) {
              updatedFormerlyListening.push(userId);
            }
          }
          
          // Update the document
          await updateDoc(completedPlanDoc.ref, {
            currentlyListening: updatedCurrentlyListening,
            formerlyListening: updatedFormerlyListening,
            updatedAt: serverTimestamp()
          });
          
          console.log(`Updated family ${familyId} completed plan ${planId} listening status for user ${userId}: ${action}`);
        }
      }
    } catch (error) {
      console.error('Error updating family completed plan listening status:', error);
    }
  };

  // Helper function to update family completed plan subscriptions
  const updateFamilyCompletedPlanSubscription = async (planId, userId, action) => {
    try {
      if (!userData || !userData.familyIds || userData.familyIds.length === 0) {
        console.log('No family IDs found for user');
        return;
      }

      // Update subscription status for each family the user belongs to
      for (const familyId of userData.familyIds) {
        // Find the completed plan document for this family
        const familyCompletedPlansSnapshot = await getDocs(
          collection(db, `families/${familyId}/completedPlans`)
        );
        
        const completedPlanDoc = familyCompletedPlansSnapshot.docs.find(
          doc => doc.data().planId === planId
        );
        
        if (completedPlanDoc) {
          const planData = completedPlanDoc.data();
          const currentlySubscribed = planData.currentlySubscribed || [];
          const formerlySubscribed = planData.formerlySubscribed || [];
          
          let updatedCurrentlySubscribed = [...currentlySubscribed];
          let updatedFormerlySubscribed = [...formerlySubscribed];
          
          if (action === 'subscribe') {
            // Add to currentlySubscribed if not already there
            if (!updatedCurrentlySubscribed.includes(userId)) {
              updatedCurrentlySubscribed.push(userId);
            }
            // Remove from formerlySubscribed if they were there
            updatedFormerlySubscribed = updatedFormerlySubscribed.filter(id => id !== userId);
          } else if (action === 'unsubscribe') {
            // Remove from currentlySubscribed
            updatedCurrentlySubscribed = updatedCurrentlySubscribed.filter(id => id !== userId);
            // Add to formerlySubscribed if not already there
            if (!updatedFormerlySubscribed.includes(userId)) {
              updatedFormerlySubscribed.push(userId);
            }
          }
          
          // Update the document
          await updateDoc(completedPlanDoc.ref, {
            currentlySubscribed: updatedCurrentlySubscribed,
            formerlySubscribed: updatedFormerlySubscribed,
            updatedAt: serverTimestamp()
          });
          
          console.log(`Updated family ${familyId} completed plan ${planId} subscription for user ${userId}: ${action}`);
        }
      }
    } catch (error) {
      console.error('Error updating family completed plan subscription:', error);
    }
  };

  // Badges functions
  const fetchBadgesData = async () => {
    if (!currentUser) return;
    
    setIsLoadingBadges(true);
    try {
      // Check for newly earned badges, then refresh
      await checkAndAwardBadges(currentUser.uid, db);

      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserBadges(userData.badges || []);
        setUserStreaks(userData.streaks || {});
      }

      await calculateCurrentStreaks();

    } catch (error) {
      console.error('Error fetching badges data:', error);
    } finally {
      setIsLoadingBadges(false);
    }
  };

  const calculateCurrentStreaks = async () => {
    try {
      const recordingsSnapshot = await getDocs(
        query(collection(db, 'recordings'), where('userId', '==', currentUser.uid))
      );
      const recordings = recordingsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const dailyStreak = calculateDailyStreak(recordings);

      await updateDoc(doc(db, 'users', currentUser.uid), {
        'streaks.dailyRecording': dailyStreak,
        'streaks.lastCalculated': serverTimestamp()
      });

      setUserStreaks(prev => ({ ...prev, dailyRecording: dailyStreak }));
    } catch (error) {
      console.error('Error calculating streaks:', error);
    }
  };

  const getBadgeIcon = (badgeType, className = 'w-7 h-7') => {
    switch (badgeType) {
      case 'first-recording':      return <MicrophoneIcon className={className} />;
      case 'recording-streak-3':
      case 'recording-streak-7':
      case 'recording-streak-30':  return <FireIcon className={className} />;
      case 'recording-bronze':
      case 'recording-silver':
      case 'recording-gold':
      case 'recording-librarian':  return <MicrophoneIcon className={className} />;
      case 'old-testament-warrior':
      case 'studio-master-nt':
      case 'studio-master-ot':
      case 'family-founders':      return <BookOpenIcon className={className} />;
      case 'studio-star':
      case 'milestone-10':
      case 'milestone-100':        return <StarIcon className={className} />;
      case 'listener-emmy':
      case 'listener-bronze':
      case 'listener-silver':
      case 'listener-gold':        return <SpeakerWaveIcon className={className} />;
      case 'family-creator':       return <UsersIcon className={className} />;
      case 'super-admin':          return <ShieldCheckIcon className={className} />;
      case 'early-adopter':        return <RocketLaunchIcon className={className} />;
      default:                     return <TrophyIcon className={className} />;
    }
  };

  // Guard against a null currentUser (e.g. during logout) before the render
  // reads currentUser.uid further down.
  if (!currentUser) {
    navigate('/auth', { replace: true });
    return null;
  }
  if (!userData) return <div className="text-center mt-10">Loading...</div>;

  return (
    <div className="max-w-md mx-auto mt-10 bg-white dark:bg-[#111827] rounded-[3px] shadow-lg p-8 pb-28">
      <div className="flex flex-col items-center mb-6">
        <ImageUpload
          currentImage={profileImageUrl}
          onUploadSuccess={handleImageUploadSuccess}
          userId={currentUser.uid}
          className="mb-2"
          size="w-24 h-24"
        />
        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{getUserDisplayName(userData)}</div>
        <div className="text-gray-600 dark:text-white/60 text-sm">{userData.email}</div>
      </div>
      <div>
        {/* Recordings */}
        <MenuSection title="Recordings">
          <MenuRow
            label="My Recordings"
            badge={myRecordingsTotal}
            onClick={() => { setRecordingsView('mine'); setPage(1); setRecordingsDrawerOpen(true); }}
          />
          <MenuRow
            label="Family Recordings"
            badge={familyRecordingsTotal}
            onClick={() => { setRecordingsView('family'); setPage(1); setRecordingsDrawerOpen(true); }}
          />
          <MenuRow label="Chapter Plans" onClick={() => navigate('/plans')} />
          <MenuRow label="Passages Plans" onClick={() => navigate('/passages-plans')} />
        </MenuSection>

        {/* Account */}
        <MenuSection title="Account">
          <MenuRow label="Listening Plans" onClick={() => navigate('/listening-plans')} />
          <MenuRow label="Badges and Streaks" onClick={() => setBadgesDrawerOpen(true)} />
          <MenuRow label="Study Notes" onClick={() => setStudyNotesDrawerOpen(true)} />
          <MenuRow label="Favorites" onClick={() => navigate('/favorites')} />
          <MenuRow label="My Profile" onClick={() => setDrawerOpen(true)} />
        </MenuSection>

        {/* Settings */}
        <MenuSection title="Settings">
          <MenuRow label="Listening & Reading" onClick={() => { setSettingsView('listening'); setNotificationsDrawerOpen(true); }} />
          <MenuRow label="Recording" onClick={() => { setSettingsView('recording'); setNotificationsDrawerOpen(true); fetchBiblePlansData(); }} />
          <MenuRow label="Audio Enhancement" onClick={() => { setSettingsView('audioEnhancement'); setNotificationsDrawerOpen(true); }} />
          <MenuRow label="Notifications" onClick={() => { setSettingsView('notifications'); setNotificationsDrawerOpen(true); }} />
          <MenuRow label="Appearance" badge={theme === 'dark' ? 'Dark' : 'Light'} onClick={() => { setSettingsView('appearance'); setNotificationsDrawerOpen(true); }} />
        </MenuSection>

        {/* Family */}
        <MenuSection title="Family">
          <MenuRow label="Reminders" onClick={() => navigate('/reminders')} />
          {currentUser?.familyIds?.length > 0 && (
            <MenuRow label="Manage Family" onClick={openManageFamily} />
          )}
          <MenuRow label="Family Invites" onClick={() => setFamilyInvitationsOpen(true)} />
          <MenuRow label="Join or Create Family" onClick={() => navigate('/join-family')} />
        </MenuSection>

        {/* More */}
        <MenuSection title="More">
          <MenuRow label="Shop" onClick={() => navigate('/shop')} />
          <MenuRow label="Help" onClick={() => { window.location.href = '/help/'; }} />
          <MenuRow label="Privacy Policy" onClick={() => window.open('/privacy.html', '_blank', 'noopener,noreferrer')} />
          <MenuRow label="Terms of Service" onClick={() => window.open('/terms.html', '_blank', 'noopener,noreferrer')} />
          <MenuRow label="Tutorial" onClick={() => navigate('/tutorial')} />
        </MenuSection>

        {/* Admin */}
        {currentUser?.superAdmin === true && (
          <MenuSection title="Admin">
            <MenuRow label="Super Admin" onClick={() => navigate('/super-admin')} />
          </MenuSection>
        )}

        {/* Logout */}
        <MenuSection>
          <MenuRow
            label="Logout"
            danger
            onClick={async () => {
              if (window.confirm('Are you sure you want to logout?')) {
                try {
                  await logout();
                  window.location.href = '/home';
                } catch (error) {
                  console.error('Error logging out:', error);
                }
              }
            }}
          />
        </MenuSection>
      </div>

      {/* Drawer for Details */}
      <Transition.Root show={drawerOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setDrawerOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6">
                      <div className="flex items-center justify-between mb-4">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">Profile Details</Dialog.Title>
                        <button
                          className="text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70"
                          onClick={() => setDrawerOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <form className="flex-1 flex flex-col space-y-4 overflow-y-auto">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">Profile Image</label>
                          <div className="flex justify-center">
                            <ImageUpload
                              currentImage={profileImageUrl}
                              onUploadSuccess={handleImageUploadSuccess}
                              userId={currentUser.uid}
                              size="w-32 h-32"
                            />
                          </div>
                          <p className="text-xs text-gray-500 dark:text-white/40 mt-2 text-center">
                            Click the image to upload a new photo. Large images will be automatically compressed.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">First Name</label>
                          <input 
                            type="text" 
                            className="input-field mt-1" 
                            value={firstName} 
                            onChange={e => {
                              setFirstName(e.target.value);
                              // Update displayName when firstName changes (only if not manually edited)
                              if (!displayNameManuallyEdited) {
                                setDisplayName(constructDisplayName(e.target.value, middleName, lastName));
                              }
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Middle Name</label>
                          <input 
                            type="text" 
                            className="input-field mt-1" 
                            value={middleName} 
                            onChange={e => {
                              setMiddleName(e.target.value);
                              // Update displayName when middleName changes (only if not manually edited)
                              if (!displayNameManuallyEdited) {
                                setDisplayName(constructDisplayName(firstName, e.target.value, lastName));
                              }
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Last Name</label>
                          <input 
                            type="text" 
                            className="input-field mt-1" 
                            value={lastName} 
                            onChange={e => {
                              setLastName(e.target.value);
                              // Update displayName when lastName changes (only if not manually edited)
                              if (!displayNameManuallyEdited) {
                                setDisplayName(constructDisplayName(firstName, middleName, e.target.value));
                              }
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Display Name</label>
                          <input 
                            type="text" 
                            className="input-field mt-1" 
                            value={displayName} 
                            onChange={e => {
                              setDisplayName(e.target.value);
                              setDisplayNameManuallyEdited(true);
                            }}
                            placeholder="Nickname, pet name, or preferred name"
                          />
                          <p className="text-xs text-gray-500 dark:text-white/40 mt-1">
                            This is how your name appears throughout the app. Great for nicknames or pet names!
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Email</label>
                          <div className="input-field mt-1 flex items-center justify-between">
                            <span className="text-gray-700 dark:text-white/70 truncate">{email}</span>
                            <button
                              type="button"
                              onClick={openEmailModal}
                              className="ml-3 flex-shrink-0 text-sm font-medium text-cerulean dark:text-aquamarine hover:underline"
                            >
                              Change
                            </button>
                          </div>
                          {emailSuccess && <p className="mt-1 text-xs text-green-600 dark:text-green-400">{emailSuccess}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Phone</label>
                          <input type="tel" className="input-field mt-1" value={phone} onChange={e => setPhone(e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Family</label>
                          <select
                            className="input-field mt-1"
                            value={selectedFamilyId}
                            onChange={e => setSelectedFamilyId(e.target.value)}
                          >
                            {familyOptions.map(fam => (
                              <option key={fam.id} value={fam.id}>{fam.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Family Relation</label>
                          <input type="text" className="input-field mt-1" value={familyRelation} onChange={e => setFamilyRelation(e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Birthday</label>
                          <input type="date" className="input-field mt-1" value={birthday} onChange={e => setBirthday(e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70">Bio</label>
                          <textarea className="input-field mt-1" value={bio} onChange={e => setBio(e.target.value)} rows={3} />
                        </div>
                        <div className="pt-4">
                          <button 
                            type="button" 
                            className="btn-primary w-full"
                            onClick={async () => {
                              try {
                                const { updateDoc } = await import('firebase/firestore');
                                const { db } = await import('../config/firebase');
                                
                                // Prepare update data
                                const updateData = {
                                  firstName: firstName.trim(),
                                  middleName: middleName.trim(),
                                  lastName: lastName.trim(),
                                  displayName: displayName.trim(),
                                  phone: phone.trim(),
                                  familyRelation: familyRelation.trim(),
                                  birthday: birthday || null,
                                  bio: bio.trim(),
                                  profileImageUrl: profileImageUrl,
                                  profileImagePath: profileImagePath
                                };
                                
                                // Update user document
                                await updateDoc(doc(db, 'users', currentUser.uid), updateData);
                                
                                // Update local state
                                setUserData(prev => ({ ...prev, ...updateData }));
                                
                
                                setDrawerOpen(false);
                              } catch (error) {
                                console.error('Error updating profile:', error);

                              }
                            }}
                          >
                            Save Changes
                          </button>
                        </div>
                      </form>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Drawer for Recordings */}
      <Transition.Root show={recordingsDrawerOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setRecordingsDrawerOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6">
                      <div className="flex items-center justify-between mb-4">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                          {recordingsView === 'family' ? 'Family' : 'My'} Recordings ({totalRecordings})
                        </Dialog.Title>
                        <div className="flex items-center space-x-2">
                          <button
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
                            onClick={() => {
                              // Force refresh of recordings
                              setPage(1);
                              setUserRecordings([]);
                              setTimeout(() => {
                                const event = new Event('focus');
                                window.dispatchEvent(event);
                              }, 100);
                            }}
                          >
                            Refresh
                          </button>
                          <button
                            className="text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70"
                            onClick={() => setRecordingsDrawerOpen(false)}
                          >
                            <span className="sr-only">Close panel</span>
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="mb-4 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Search Recordings</label>
                          <input
                            type="text"
                            className="input-field"
                            placeholder="Search by passage, book, chapter, or verse..."
                            value={searchTerm}
                            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                          />
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Book</label>
                            <select
                              className="input-field"
                              value={bookFilter}
                              onChange={e => { setBookFilter(e.target.value); setChapterFilter(''); setPage(1); }}
                            >
                              <option value="">All Books</option>
                              {BIBLE_BOOKS.map(book => (
                                <option key={book.id} value={book.id}>{book.name}</option>
                              ))}
                            </select>
                          </div>
                          {bookFilter && (
                            <div className="w-28">
                              <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Chapter</label>
                              <select
                                className="input-field"
                                value={chapterFilter}
                                onChange={e => { setChapterFilter(e.target.value); setPage(1); }}
                              >
                                <option value="">All</option>
                                {[...new Set(
                                  allRecordings
                                    .filter(r => r.book === bookFilter && r.chapter)
                                    .map(r => Number(r.chapter))
                                )].sort((a, b) => a - b).map(ch => (
                                  <option key={ch} value={ch}>{ch}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-4 flex-1 overflow-y-auto">
                        {userRecordings.length === 0 ? (
                          <div className="text-gray-500 dark:text-white/40 text-center py-8">No recordings found.</div>
                        ) : (
                                                  userRecordings.map(rec => (
                          <div key={rec.id} className="bg-gray-50 dark:bg-white/5 p-4 rounded-[3px]">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="text-sm text-gray-700 dark:text-white/70 font-medium">{rec.passage}</div>
                                {recordingsView === 'family' && rec.name && (
                                  <div className="text-xs text-gray-500 dark:text-white/40">{rec.name}</div>
                                )}
                              </div>
                              {rec.userId === currentUser.uid && (
                                <button
                                  onClick={() => handleDeleteRecording(rec)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-400/10 inline-flex items-center gap-1"
                                  title="Delete recording"
                                >
                                  <TrashIcon className="w-4 h-4" /> Delete
                                </button>
                              )}
                            </div>
                            <audio key={rec.audioURL || rec.audioUrl} controls className="w-full">
                              <source src={rec.audioURL || rec.audioUrl} />
                              Your browser does not support the audio element.
                            </audio>
                            {rec.userId === currentUser.uid && rec.trimmed && expandedTrimId !== rec.id && (
                              <div className="mt-2">
                                {restoringId === rec.id ? (
                                  <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-500" />
                                    Restoring original…
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => handleRestore(rec)}
                                    disabled={!!restoringId || !!trimSavingId}
                                    className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Restore original
                                  </button>
                                )}
                              </div>
                            )}
                            {rec.userId === currentUser.uid && expandedTrimId === rec.id && (
                              <WaveformTrimmer
                                audioUrl={rec.audioURL || rec.audioUrl}
                                onTrim={(start, end) => handleApplyTrim(rec, start, end)}
                                onCancel={() => setExpandedTrimId(null)}
                                isSaving={trimSavingId === rec.id}
                              />
                            )}
                            {rec.userId === currentUser.uid && expandedTrimId !== rec.id && (
                              reRecordProcessing === rec.id ? (
                                // ── State 4: saving ──
                                <div className="mt-3 flex items-center gap-2 text-sm text-cerulean">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cerulean" />
                                  <span>Saving new recording…</span>
                                </div>
                              ) : reRecordingId === rec.id ? (
                                // ── State 3: actively recording ──
                                <div className="mt-3">
                                  {rec.book && rec.chapter && (rec.verse || rec.verseNumber) && (
                                    <p className="text-sm text-gray-600 dark:text-white/60 leading-snug italic bg-gray-50 dark:bg-white/5 rounded-[3px] px-3 py-2 mb-3">
                                      {getVerseText(rec.book, rec.chapter, rec.verse || rec.verseNumber)?.replace(/^["']|["']$/g, '')}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 bg-red-50 dark:bg-red-400/10 border border-red-200 dark:border-red-400/25 rounded-[3px] px-3 py-2.5">
                                    <div className="flex items-center gap-2 flex-1">
                                      <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                                      <span className="text-sm font-mono font-semibold text-red-700 dark:text-red-300">
                                        {String(Math.floor(reRecordTime / 60)).padStart(2, '0')}:{String(reRecordTime % 60).padStart(2, '0')}
                                      </span>
                                      <span className="text-xs text-red-400 dark:text-red-300/70">Recording…</span>
                                    </div>
                                    <button
                                      onClick={stopProfileReRecord}
                                      className="flex items-center gap-1.5 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-[3px] transition-colors"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                        <rect x="6" y="6" width="12" height="12" rx="1" />
                                      </svg>
                                      Stop Recording
                                    </button>
                                  </div>
                                </div>
                              ) : expandedReRecordId === rec.id ? (
                                // ── State 2: expanded, ready to start ──
                                <div className="mt-3">
                                  {rec.book && rec.chapter && (rec.verse || rec.verseNumber) && (
                                    <p className="text-sm text-gray-600 dark:text-white/60 leading-snug italic bg-gray-50 dark:bg-white/5 rounded-[3px] px-3 py-2 mb-3">
                                      {getVerseText(rec.book, rec.chapter, rec.verse || rec.verseNumber)?.replace(/^["']|["']$/g, '')}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => { setExpandedReRecordId(null); startProfileReRecord(rec.id); }}
                                      className="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-semibold py-2.5 rounded-[3px] transition-colors text-sm"
                                    >
                                      <div className="w-3 h-3 bg-white rounded-full" />
                                      Start Recording
                                    </button>
                                    <button
                                      onClick={() => setExpandedReRecordId(null)}
                                      className="px-4 py-2.5 text-sm text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70 transition-colors font-medium"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                // ── State 1: idle actions ──
                                <div className="mt-2 flex items-center gap-3">
                                  <button
                                    onClick={() => setExpandedReRecordId(rec.id)}
                                    disabled={!!reRecordingId || !!reRecordProcessing}
                                    className="flex items-center gap-1.5 text-xs text-cerulean hover:text-space-cadet dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                    </svg>
                                    Re-record
                                  </button>
                                  <span className="text-gray-200 dark:text-white/20 text-xs">|</span>
                                  <button
                                    onClick={() => setExpandedTrimId(rec.id)}
                                    disabled={!!reRecordingId || !!reRecordProcessing}
                                    className="flex items-center gap-1.5 text-xs text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243zm0-5.758L6.343 6.343" />
                                    </svg>
                                    Edit Audio
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        ))
                        )}
                      </div>
                      {/* Pagination */}
                      <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                        <button
                          className="btn-secondary px-4 py-2"
                          disabled={page === 1}
                          onClick={() => setPage(page - 1)}
                        >
                          Previous
                        </button>
                        <div className="text-center">
                          <div className="text-sm text-gray-700 dark:text-white/70">
                            Page {page} of {totalPages}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-white/40">
                            Showing {userRecordings.length} of {totalRecordings} recordings
                          </div>
                        </div>
                        <button
                          className="btn-secondary px-4 py-2"
                          disabled={page === totalPages}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Drawer for Notifications */}
      <Transition.Root show={notificationsDrawerOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setNotificationsDrawerOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 pl-10 max-w-md flex">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6">
                      <div className="flex items-center justify-between mb-4">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                          {settingsView === 'recording' ? 'Recording' : settingsView === 'listening' ? 'Listening' : settingsView === 'appearance' ? 'Appearance' : settingsView === 'audioEnhancement' ? 'Audio Enhancement' : 'Notifications'}
                        </Dialog.Title>
                        <button
                          className="text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70"
                          onClick={() => setNotificationsDrawerOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex-1 flex flex-col space-y-6 overflow-y-auto">
                        {settingsView === 'recording' && (
                        <>
                        {/* Recording plan */}
                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7l-3 3-1.5-1.5" />
                            </svg>
                          }
                          title="Chapter Plan"
                          description="Follow a guided plan, or record freely."
                        >
                          {selectedRecordingPlan ? (
                            <div className="flex items-center gap-2 rounded-[3px] bg-cerulean/10 border border-cerulean/30 px-3 py-2">
                              <CheckIcon className="w-4 h-4 text-cerulean flex-shrink-0" />
                              <span className="text-sm font-semibold text-cerulean truncate">
                                On plan: {availablePlans.find(p => p.id === selectedRecordingPlan)?.name || 'Current plan'}
                              </span>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">No plan — you're recording freely.</p>
                          )}
                          <select
                            value={selectedRecordingPlan}
                            onChange={(e) => handleRecordingPlanChange(e.target.value)}
                            disabled={isLoadingPlans}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-white/15 rounded-[3px] text-ink dark:text-white bg-white dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-cerulean focus:border-transparent disabled:opacity-50"
                          >
                            <option value="">No plan (record freely)</option>
                            {availablePlans.map((plan) => (
                              <option key={plan.id} value={plan.id}>{plan.name}</option>
                            ))}
                          </select>
                          {selectedRecordingPlan && (
                            <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                              {availablePlans.find(p => p.id === selectedRecordingPlan)?.description}
                            </p>
                          )}
                          <button
                            onClick={() => navigate('/plans')}
                            className="text-xs text-cerulean hover:text-cerulean/80 transition-colors text-left"
                          >
                            Manage plans on the Bible Plans page →
                          </button>
                        </SettingsSection>

                        {/* Default record point */}
                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                            </svg>
                          }
                          title="Default Record Point"
                          description="With no active plan, where should Record start when you return?"
                        >
                          <select
                            value={defaultRecordPoint}
                            onChange={(e) => setDefaultRecordPoint(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-white/15 rounded-[3px] text-ink dark:text-white bg-white dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-cerulean focus:border-transparent"
                          >
                            <option value="last-recorded">From last chapter recorded</option>
                            <option value="whole-bible">Whole Bible (in order)</option>
                          </select>
                          <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                            {defaultRecordPoint === 'last-recorded'
                              ? 'Continues from the chapter you most recently recorded — the next unrecorded verse, or the next chapter once it’s done.'
                              : 'Goes to the first unrecorded verse scanning the whole Bible from Genesis.'}
                          </p>
                        </SettingsSection>

                        {/* Auto-enhance recordings */}
                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          }
                          title="Auto-enhance my recordings"
                          description="After each upload, automatically process your audio to boost volume, cut rumble, and even out dynamics. Original file is always kept."
                        >
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-xs text-gray-500 dark:text-white/40 leading-snug flex-1">
                              {autoEnhanceRecordings
                                ? 'On — your recordings will be processed after upload. Listeners can choose which version to hear.'
                                : 'Off — your recordings are stored exactly as recorded.'}
                            </p>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={autoEnhanceRecordings}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                autoEnhanceRecordings ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setAutoEnhanceRecordings(!autoEnhanceRecordings)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  autoEnhanceRecordings ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </SettingsSection>

                        {/* Show cassette */}
                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-4M9 17l3 3m0 0l3-3m-3 3V9" />
                            </svg>
                          }
                          title="Show cassette on Record page"
                          description="Display the retro cassette tape visual while recording."
                        >
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-xs text-gray-500 dark:text-white/40 leading-snug flex-1">
                              {showCassette ? 'On — the cassette is shown.' : 'Off — recording controls only.'}
                            </p>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={showCassette}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                showCassette ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setShowCassette(!showCassette)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  showCassette ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </SettingsSection>

                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V6a2 2 0 012-2h12a2 2 0 012 2v2M4 8h16M4 8v10a2 2 0 002 2h12a2 2 0 002-2V8" />
                            </svg>
                          }
                          title="Cassette preference"
                          description="Choose the default cassette size while recording."
                        >
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-xs text-gray-500 dark:text-white/40 leading-snug flex-1">
                              {cassetteCompact ? 'Compact strip — more room for verse text while recording.' : 'Full cassette with reels displayed when space allows.'}
                            </p>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={!cassetteCompact}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                !cassetteCompact ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setCassetteCompact(!cassetteCompact)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  !cassetteCompact ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </SettingsSection>

                        {/* Bible translation */}
                        <SettingsSection
                          icon={<BookOpenIcon className="w-5 h-5" />}
                          title="Bible Translation"
                          description="The translation you record in (also used for reading on Listen)."
                        >
                          <select
                            value={translation}
                            onChange={(e) => changeTranslation(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-white/15 rounded-[3px] text-ink dark:text-white bg-white dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-cerulean focus:border-transparent"
                          >
                            {translations.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.abbreviation})
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                            Recordings are shared across translations — switching only changes the on-screen text, not the audio.
                          </p>
                        </SettingsSection>

                        {/* Verses per block */}
                        <SettingsSection
                          icon={<BookOpenIcon className="w-5 h-5" />}
                          title="Verses per Block"
                          description="How many verses flow together before a line break on the Record page."
                        >
                          <div className="flex gap-1.5">
                            {[1, 2, 3, 4, 5, 6, 7].map(n => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setRecordVersesPerBlock(n)}
                                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                  recordVersesPerBlock === n
                                    ? 'bg-cerulean text-white'
                                    : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/15'
                                }`}
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                            {recordVersesPerBlock === 1
                              ? 'Each verse starts on its own line — easiest to follow along.'
                              : `Verses group in blocks of ${recordVersesPerBlock}, like paragraphs.`}
                          </p>
                        </SettingsSection>
                        </>
                        )}

                        {settingsView === 'audioEnhancement' && (
                        <>
                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          }
                          title="Auto-Enhance Recordings"
                          description="After each upload, automatically process your audio to boost volume, cut rumble, and even out dynamics. Original file is always kept."
                        >
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-xs text-gray-500 dark:text-white/40 leading-snug flex-1">
                              {autoEnhanceRecordings
                                ? 'On — your recordings will be processed after upload. Listeners can choose which version to hear.'
                                : 'Off — your recordings are stored exactly as recorded.'}
                            </p>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={autoEnhanceRecordings}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                autoEnhanceRecordings ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setAutoEnhanceRecordings(!autoEnhanceRecordings)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  autoEnhanceRecordings ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </SettingsSection>

                        <fieldset disabled={!autoEnhanceRecordings} className={autoEnhanceRecordings ? '' : 'opacity-40 pointer-events-none'}>
                          <AudioEnhancementFields
                            profile={audioEnhancementProfile}
                            updateSection={updateAudioProfileSection}
                            updateEqBand={updateAudioProfileEqBand}
                            applyVoicePreset={applyAudioProfileVoicePreset}
                            currentUser={currentUser}
                          />
                        </fieldset>

                        <button
                          type="button"
                          onClick={() => setAudioEnhancementProfile(DEFAULT_AUDIO_PROFILE)}
                          className="text-xs text-cerulean hover:text-cerulean/80 transition-colors text-left"
                        >
                          Reset to defaults
                        </button>
                        </>
                        )}

                        {settingsView === 'listening' && (
                        <SettingsSection
                          icon={<SpeakerWaveIcon className="w-5 h-5" />}
                          title="Default Listen Point"
                          description="With no active plan, where should Listen start when you return?"
                        >
                          <select
                            value={defaultListenPoint}
                            onChange={(e) => setDefaultListenPoint(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-white/15 rounded-[3px] text-ink dark:text-white bg-white dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-cerulean focus:border-transparent"
                          >
                            <option value="last-listened">Last place I listened from</option>
                            <option value="whole-bible">Jump to next in Whole Bible</option>
                          </select>
                          <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                            {defaultListenPoint === 'last-listened'
                              ? 'Resumes the last passage you were listening to.'
                              : 'Advances to the next chapter in canonical order each time you return.'}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                            With an active listening plan, Listen always resumes your place in the plan.
                          </p>
                        </SettingsSection>
                        )}

                        {settingsView === 'listening' && (
                        <SettingsSection
                          icon={<BookOpenIcon className="w-5 h-5" />}
                          title="Verses per Block"
                          description="How many verses flow together before a line break on the Listen page."
                        >
                          <div className="flex gap-1.5">
                            {[1, 2, 3, 4, 5, 6, 7].map(n => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setListenVersesPerBlock(n)}
                                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                  listenVersesPerBlock === n
                                    ? 'bg-cerulean text-white'
                                    : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/15'
                                }`}
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                            {listenVersesPerBlock === 1
                              ? 'Each verse starts on its own line — easiest to follow along.'
                              : `Verses group in blocks of ${listenVersesPerBlock}, like paragraphs.`}
                          </p>
                        </SettingsSection>
                        )}

                        {settingsView === 'listening' && (
                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          }
                          title="Enhance Audio"
                          description="Boost voices and cut rumble while you listen. Applied live in your browser — recordings themselves are never modified."
                        >
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-xs text-gray-500 dark:text-white/40 leading-snug flex-1">
                              {enhanceAudio ? 'On — playback is enhanced.' : 'Off — playback uses the original recording.'}
                            </p>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={enhanceAudio}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                enhanceAudio ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setEnhanceAudio(!enhanceAudio)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  enhanceAudio ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </SettingsSection>
                        )}

                        {settingsView === 'appearance' && (
                        <SettingsSection
                          icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          }
                          title="Theme"
                          description="Choose how the app looks."
                        >
                          {[
                            { id: 'light', title: 'Light', desc: 'Bright, high-contrast look for daytime reading.' },
                            { id: 'dark', title: 'Dark', desc: 'Dimmed, low-glare look — easier on the eyes at night.' },
                          ].map((opt) => {
                            const selected = theme === opt.id;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => setTheme(opt.id)}
                                className={`w-full text-left p-3 rounded-[3px] border-2 transition-colors flex items-start gap-3 ${
                                  selected ? 'border-cerulean bg-cerulean/5' : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
                                }`}
                              >
                                <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center border-2 ${selected ? 'border-cerulean bg-cerulean text-white' : 'border-gray-300 dark:border-white/15'}`}>
                                  {selected && <CheckIcon className="w-3 h-3" />}
                                </span>
                                <span className="min-w-0">
                                  <span className="block text-sm font-medium text-gray-900 dark:text-white">{opt.title}</span>
                                  <span className="block text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">{opt.desc}</span>
                                </span>
                              </button>
                            );
                          })}
                          <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                            Dark mode is new — right now it's fully styled on Listen, and applies app-wide as more pages get their dark styling.
                          </p>
                        </SettingsSection>
                        )}

                        {settingsView === 'notifications' && (
                        <SettingsSection
                          icon={<BellIcon className="w-5 h-5" />}
                          title="Notifications"
                          description="Choose what you'd like to hear about from your family."
                        >
                          {/* Channels */}
                          <div className="pb-1">
                            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-white/30 mb-2">Channels</p>
                            <div className="flex items-center justify-between gap-4 py-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</div>
                                <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">
                                  Receive notifications by email.
                                </p>
                              </div>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={emailEnabled}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                  emailEnabled ? 'bg-cerulean' : 'bg-timberwolf'
                                }`}
                                onClick={() => setEmailEnabled(!emailEnabled)}
                              >
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${emailEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                              </button>
                            </div>
                            <div className="flex items-center justify-between gap-4 py-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">Push Notifications</div>
                                <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">
                                  Receive notifications on the mobile app.
                                </p>
                              </div>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={pushEnabled}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                  pushEnabled ? 'bg-cerulean' : 'bg-timberwolf'
                                }`}
                                onClick={() => setPushEnabled(!pushEnabled)}
                              >
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${pushEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                              </button>
                            </div>
                          </div>

                          <div className="border-t border-gray-100 dark:border-white/10 mt-1 mb-3"></div>

                          {/* Recording Reminders */}
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">Recording Reminders</p>
                              <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5">Get a personal nudge on the days and time you choose. Fires on your mobile device.</p>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={reminderEnabled}
                              onClick={() => setReminderEnabled(v => !v)}
                              className={`relative ml-4 flex-shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${reminderEnabled ? 'bg-cerulean' : 'bg-gray-300 dark:bg-white/20'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${reminderEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          {reminderEnabled && (
                            <>
                              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Days</p>
                              <div className="flex gap-1.5 mb-4">
                                {[
                                  { key: 'mon', label: 'M' },
                                  { key: 'tue', label: 'T' },
                                  { key: 'wed', label: 'W' },
                                  { key: 'thu', label: 'H' },
                                  { key: 'fri', label: 'F' },
                                  { key: 'sat', label: 'S' },
                                  { key: 'sun', label: 'S' },
                                ].map((d) => {
                                  const active = reminderDays.includes(d.key);
                                  return (
                                    <button
                                      key={d.key}
                                      type="button"
                                      onClick={() => setReminderDays(prev =>
                                        prev.includes(d.key) ? prev.filter(x => x !== d.key) : [...prev, d.key]
                                      )}
                                      className={`h-9 w-9 rounded-full text-sm font-bold transition-colors ${
                                        active
                                          ? 'bg-cerulean text-white'
                                          : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/20'
                                      }`}
                                    >
                                      {d.label}
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Time</p>
                              <input
                                type="time"
                                value={`${String(reminderHour).padStart(2, '0')}:${String(reminderMinute).padStart(2, '0')}`}
                                onChange={(e) => {
                                  const [h, m] = e.target.value.split(':').map(Number);
                                  setReminderHour(h);
                                  setReminderMinute(m);
                                }}
                                className="input-field w-auto"
                              />
                            </>
                          )}

                          <div className="border-t border-gray-100 dark:border-white/10 mt-3 mb-1"></div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-white/30 pt-2 pb-1">Notify Me When</p>

                          {/* New recordings toggle */}
                          <div className="flex items-center justify-between gap-4 py-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">New Recordings</div>
                              <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">
                                When a family member uploads a new recording.
                              </p>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={newRecordingsNotification}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                newRecordingsNotification ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setNewRecordingsNotification(!newRecordingsNotification)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  newRecordingsNotification ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>

                          <div className="border-t border-gray-100 dark:border-white/10"></div>

                          {/* Study notes toggle */}
                          <div className="flex items-center justify-between gap-4 py-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">Study Notes</div>
                              <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">
                                When a family member adds a study note.
                              </p>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={studyNotesNotification}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                studyNotesNotification ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setStudyNotesNotification(!studyNotesNotification)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  studyNotesNotification ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>

                          <div className="border-t border-gray-100 dark:border-white/10"></div>

                          {/* Listening activity toggle */}
                          <div className="flex items-center justify-between gap-4 py-2">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">Someone Listens to My Recording</div>
                              <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">
                                When a family member listens to a chapter you recorded.
                              </p>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={listeningActivityNotification}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cerulean focus:ring-offset-2 ${
                                listeningActivityNotification ? 'bg-cerulean' : 'bg-timberwolf'
                              }`}
                              onClick={() => setListeningActivityNotification(!listeningActivityNotification)}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  listeningActivityNotification ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>

                          {/* Frequency - only when new-recording notifications are on */}
                          {newRecordingsNotification && (
                            <div className="border-t border-gray-100 dark:border-white/10 pt-3 space-y-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">How often?</div>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  className={`px-3 py-2 text-xs font-medium rounded-[3px] border-2 transition-colors ${
                                    notificationFrequency === 'daily'
                                      ? 'border-cerulean bg-cerulean/5 text-cerulean'
                                      : 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-white/60 hover:bg-gray-50 dark:hover:bg-white/5'
                                  }`}
                                  onClick={() => setNotificationFrequency('daily')}
                                >
                                  Daily digest
                                </button>
                                <button
                                  type="button"
                                  className={`px-3 py-2 text-xs font-medium rounded-[3px] border-2 transition-colors ${
                                    notificationFrequency === 'immediate'
                                      ? 'border-cerulean bg-cerulean/5 text-cerulean'
                                      : 'border-gray-200 dark:border-white/10 text-gray-600 dark:text-white/60 hover:bg-gray-50 dark:hover:bg-white/5'
                                  }`}
                                  onClick={() => setNotificationFrequency('immediate')}
                                >
                                  Every recording
                                </button>
                              </div>
                              <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                                {notificationFrequency === 'daily'
                                  ? 'One summary per day with all new recordings.'
                                  : 'A notification for each new recording as it arrives.'}
                              </p>
                            </div>
                          )}
                        </SettingsSection>
                        )}
                        <div className="pt-4">
                          <button
                            type="button"
                            className="bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white font-semibold rounded-[3px] py-3 transition-all duration-200 shadow-sm hover:shadow-md w-full"
                            onClick={async () => {
                              try {
                                const { updateDoc } = await import('firebase/firestore');
                                const { db } = await import('../config/firebase');
                                
                                // Prepare update data
                                const updateData = {
                                  defaultRecordPoint,
                                  defaultListenPoint,
                                  recordVersesPerBlock,
                                  listenVersesPerBlock,
                                  'recordingPreferences.autoEnhanceRecordings': autoEnhanceRecordings,
                                  'recordingPreferences.audioEnhancementProfile': audioEnhancementProfile,
                                  'recordingPreferences.showCassette': showCassette,
                                  'recordingPreferences.cassetteCompact': cassetteCompact,
                                  'playbackPreferences.enhanceAudio': enhanceAudio,
                                  notificationPreferences: {
                                    emailEnabled,
                                    pushEnabled,
                                    newRecordings: newRecordingsNotification,
                                    studyNotes: studyNotesNotification,
                                    listeningActivity: listeningActivityNotification,
                                    frequency: notificationFrequency,
                                    recordingReminder: {
                                      enabled: reminderEnabled,
                                      days: reminderDays,
                                      hour: reminderHour,
                                      minute: reminderMinute,
                                    },
                                  }
                                };

                                // Update user document
                                await updateDoc(doc(db, 'users', currentUser.uid), updateData);

                                // Update local state
                                setUserData(prev => ({
                                  ...prev,
                                  defaultRecordPoint,
                                  defaultListenPoint,
                                  recordVersesPerBlock,
                                  listenVersesPerBlock,
                                  notificationPreferences: updateData.notificationPreferences
                                }));

                                // Propagate to AuthContext so the Record page
                                // immediately uses the new recording scope.
                                if (refreshUserData) await refreshUserData();
                                

                                setNotificationsDrawerOpen(false);
                              } catch (error) {
                                console.error('Error updating notification preferences:', error);

                              }
                            }}
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Drawer for Family Invitations */}
      <Transition.Root show={familyInvitationsOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setFamilyInvitationsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 pl-10 max-w-md flex">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6">
                      <div className="flex items-center justify-between mb-4">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">Family Invitations & Requests</Dialog.Title>
                        <button
                          className="text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70"
                          onClick={() => setFamilyInvitationsOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        <FamilyInvitations />
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Manage Family drawer */}
      <Transition.Root show={manageFamilyOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setManageFamilyOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in-out duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full" enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0" leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6 overflow-y-auto">
                      <div className="flex items-center justify-between mb-6">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                          My Families
                        </Dialog.Title>
                        <button
                          className="text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70"
                          onClick={() => setManageFamilyOpen(false)}
                        >
                          <span className="sr-only">Close</span>
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      {manageFamilyLoading ? (
                        <div className="flex-1 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean" />
                        </div>
                      ) : manageFamilyList.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-white/40">You're not in any families yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {manageFamilyList.map(f => {
                            const memberCount = (f.memberIDs || f.memberIds || []).length;
                            return (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => {
                                  setManageFamilyOpen(false);
                                  navigate('/family', { state: { familyId: f.id, tab: 'settings' } });
                                }}
                                className="w-full flex items-center gap-4 p-4 rounded-[3px] bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-left"
                              >
                                {f.familyPhotoUrl ? (
                                  <img src={f.familyPhotoUrl} alt={f.name} className="h-12 w-12 rounded-full object-cover flex-shrink-0" />
                                ) : (
                                  <div className="h-12 w-12 rounded-full bg-cerulean flex items-center justify-center flex-shrink-0">
                                    <UsersIcon className="h-6 w-6 text-white" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-white truncate">{f.name}</span>
                                    {f.isAdmin && (
                                      <span className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-aquamarine/20 text-aquamarine uppercase tracking-wide">
                                        Admin
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {memberCount} member{memberCount !== 1 ? 's' : ''}
                                    {f.bio ? ` · ${f.bio}` : ''}
                                  </p>
                                </div>
                                <svg className="h-4 w-4 text-gray-300 dark:text-white/25 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Email change modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEmailModalOpen(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-[#0d1520] rounded-[3px] shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Change Email</h2>
            <p className="text-sm text-gray-500 dark:text-white/40 mb-5">
              Current: <span className="font-medium text-gray-700 dark:text-white/70">{currentUser?.email}</span>
            </p>
            <form onSubmit={handleEmailConfirm} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">New email</label>
                <input
                  type="email"
                  autoFocus
                  className="input-field w-full"
                  placeholder="newemail@example.com"
                  value={newEmailValue}
                  onChange={e => { setNewEmailValue(e.target.value); setEmailError(''); }}
                />
              </div>
              {needsReauth && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Current password</label>
                  <p className="text-xs text-gray-500 dark:text-white/40 mb-1">For security, please confirm your password to continue.</p>
                  <input
                    type="password"
                    className="input-field w-full"
                    placeholder="Your current password"
                    value={emailPassword}
                    onChange={e => { setEmailPassword(e.target.value); setEmailError(''); }}
                  />
                </div>
              )}
              {emailError && <p className="text-sm text-red-500">{emailError}</p>}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setEmailModalOpen(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-white/60 bg-gray-100 dark:bg-white/10 rounded-[3px] hover:bg-gray-200 dark:hover:bg-white/15"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={emailChanging}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-cerulean rounded-[3px] hover:bg-cerulean/90 disabled:opacity-60"
                >
                  {emailChanging ? 'Saving…' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Study Notes Manager */}
      <StudyNotesManager
        isOpen={studyNotesDrawerOpen}
        onClose={() => setStudyNotesDrawerOpen(false)}
        currentUser={currentUser}
      />

      {/* Letter Box */}
      <LetterBox
        open={letterBoxOpen}
        onClose={() => setLetterBoxOpen(false)}
        initialTab={letterBoxTab}
        currentUser={currentUser}
      />

      {/* Delete Confirmation Modal */}
      <Transition.Root show={deleteModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={() => setDeleteModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-[3px] bg-white dark:bg-[#111827] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-400/15 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-2.032-1.5-2.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                        Delete Recording
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-white/40">
                          Are you sure you want to delete this recording? This action cannot be undone.
                        </p>
                        {recordingToDelete && (
                          <div className="mt-3 p-3 bg-gray-50 dark:bg-white/5 rounded-[3px]">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{recordingToDelete.passage}</p>
                            <p className="text-xs text-gray-500 dark:text-white/40">
                              Recorded by: {recordingToDelete.name || recordingToDelete.userEmail}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-[3px] bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      onClick={confirmDeleteRecording}
                    >
                      Delete Recording
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-[3px] bg-white dark:bg-white/10 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-white/20 hover:bg-gray-50 dark:hover:bg-white/5 sm:mt-0 sm:w-auto"
                      onClick={() => setDeleteModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Drawer for Bible Plans */}
      <Transition.Root show={biblePlansDrawerOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setBiblePlansDrawerOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 pl-10 max-w-md flex">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6">
                      <div className="flex items-center justify-between mb-4">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">Bible Plans</Dialog.Title>
                        <button
                          className="text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70"
                          onClick={() => setBiblePlansDrawerOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <div className="flex-1 overflow-y-auto space-y-6">
                        {isLoadingPlans ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                            <span className="ml-3 text-gray-600 dark:text-white/60">Loading plans...</span>
                          </div>
                        ) : (
                          <>
                            {/* Recording plan */}
                            <SettingsSection
                              icon={<MicrophoneIcon className="w-5 h-5" />}
                              title="Chapter Plan"
                              description="Choose a plan to guide your recording (one at a time)."
                            >
                              {selectedRecordingPlan ? (
                                <div className="flex items-center gap-2 rounded-[3px] bg-cerulean/10 border border-cerulean/30 px-3 py-2">
                                  <CheckIcon className="w-4 h-4 text-cerulean flex-shrink-0" />
                                  <span className="text-sm font-semibold text-cerulean truncate">
                                    On plan: {availablePlans.find(p => p.id === selectedRecordingPlan)?.name}
                                  </span>
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">No recording plan — you're recording freely.</p>
                              )}
                              <select
                                value={selectedRecordingPlan}
                                onChange={(e) => handleRecordingPlanChange(e.target.value)}
                                disabled={isLoadingPlans}
                                className="w-full px-3 py-2.5 border border-gray-300 dark:border-white/15 rounded-[3px] text-ink dark:text-white bg-white dark:bg-white/5 focus:outline-none focus:ring-2 focus:ring-cerulean focus:border-transparent disabled:opacity-50"
                              >
                                <option value="">No plan (record freely)</option>
                                {availablePlans.map((plan) => (
                                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                                ))}
                              </select>
                              {selectedRecordingPlan && (
                                <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">
                                  {availablePlans.find(p => p.id === selectedRecordingPlan)?.description}
                                </p>
                              )}
                            </SettingsSection>

                            {/* Plan progress report */}
                            <SettingsSection
                              icon={<TrophyIcon className="w-5 h-5" />}
                              title="Your Plans"
                              description="Your active recording and listening plans."
                            >
                              {/* Recording plan progress */}
                              <div className="space-y-1.5">
                                <div className="text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wide">Recording</div>
                                {(() => {
                                  const rp = recordingPlans.find(p => p.status === 'active') || recordingPlans[0];
                                  if (!rp) return <p className="text-xs text-gray-400 dark:text-white/35">No recording plan selected.</p>;
                                  const pd = availablePlans.find(p => p.id === rp.planId);
                                  const total = rp.totalChapters || pd?.chapters?.length || 0;
                                  // Count chapters complete via plan doc OR family completion data
                                  const planChapterKeys = new Set((pd?.chapters || []).map(c => `${c.book}_${c.chapter}`));
                                  const completedKeys = new Set([
                                    ...(rp.completedChapters || []).filter(k => planChapterKeys.has(k)),
                                    ...(planFamilyCompletionData?.chapters || [])
                                      .filter(c => c.completed && planChapterKeys.has(`${c.book}_${c.chapter}`))
                                      .map(c => `${c.book}_${c.chapter}`)
                                  ]);
                                  const done = completedKeys.size;
                                  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
                                  const complete = total > 0 && done >= total;
                                  return (
                                    <div className="space-y-1">
                                      <div className="flex items-center justify-between text-xs gap-2">
                                        <span className="font-medium text-gray-900 dark:text-white truncate">{pd?.name || 'Recording plan'}</span>
                                        <span className={`flex-shrink-0 ${complete ? 'text-cerulean font-semibold' : 'text-gray-500 dark:text-white/40'}`}>
                                          {complete ? 'Complete' : `${done}/${total}`}
                                        </span>
                                      </div>
                                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-white/5 overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-cerulean to-aqua-deep" style={{ width: `${pct}%` }} />
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Listening plan progress */}
                              <div className="space-y-1.5">
                                <div className="text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wide">Listening</div>
                                {(() => {
                                  const lp = listeningPlans[0];
                                  if (!lp) return <p className="text-xs text-gray-400 dark:text-white/35">Not subscribed to a listening plan.</p>;
                                  const pd = availablePlans.find(p => p.id === lp.planId);
                                  const done = lp.listenedChapters?.length || 0;
                                  const total = lp.totalChapters || pd?.chapters?.length || 0;
                                  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
                                  const complete = total > 0 && done >= total;
                                  return (
                                    <div className="space-y-1">
                                      <div className="flex items-center justify-between text-xs gap-2">
                                        <span className="font-medium text-gray-900 dark:text-white truncate">{pd?.name || 'Listening plan'}</span>
                                        <span className={`flex-shrink-0 ${complete ? 'text-cerulean font-semibold' : 'text-gray-500 dark:text-white/40'}`}>
                                          {complete ? 'Complete' : `${done}/${total}`}
                                        </span>
                                      </div>
                                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-white/5 overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-cerulean to-aqua-deep" style={{ width: `${pct}%` }} />
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {completedPlans.length > 0 && (
                                <div className="pt-1">
                                  <div className="text-xs font-medium text-gray-900 dark:text-white mb-1.5">Completed plans</div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {completedPlans.map((cp) => (
                                      <span key={cp.id} className="inline-flex items-center gap-1 rounded-full bg-cerulean/10 text-cerulean text-xs px-2 py-0.5">
                                        <CheckIcon className="w-3 h-3" />{cp.planName}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </SettingsSection>

                            {/* Listening plans */}
                            <SettingsSection
                              icon={<SpeakerWaveIcon className="w-5 h-5" />}
                              title="Listening Plans"
                              description="Subscribe to a completed plan for guided listening (one at a time)."
                            >
                              {/* Search */}
                              <div className="relative">
                                <svg className="w-4 h-4 text-gray-400 dark:text-white/35 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                                </svg>
                                <input
                                  type="text"
                                  value={planSearch}
                                  onChange={(e) => setPlanSearch(e.target.value)}
                                  placeholder="Search plans…"
                                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-white/15 rounded-[3px] text-sm focus:outline-none focus:ring-2 focus:ring-cerulean focus:border-transparent"
                                />
                              </div>

                              {/* Available plans */}
                              {(() => {
                                const q = planSearch.trim().toLowerCase();
                                const filtered = completedPlans.filter(cp =>
                                  !q ||
                                  (cp.planName || '').toLowerCase().includes(q) ||
                                  (cp.planDescription || '').toLowerCase().includes(q)
                                );
                                if (completedPlans.length === 0) {
                                  return <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">No completed plans available for listening yet.</p>;
                                }
                                if (filtered.length === 0) {
                                  return <p className="text-xs text-gray-400 dark:text-white/35 leading-snug">No plans match “{planSearch}”.</p>;
                                }
                                return filtered.map((cp) => {
                                  const isSubscribed = listeningPlans.some(lp => lp.planId === cp.planId);
                                  return (
                                    <div key={cp.id} className="rounded-[3px] border border-gray-200 dark:border-white/10 p-3 space-y-2">
                                      <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-white">{cp.planName}</div>
                                        <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-snug">{cp.planDescription}</p>
                                      </div>
                                      {isSubscribed ? (
                                        <button
                                          onClick={() => handleListeningPlanUnsubscribe(cp.planId)}
                                          disabled={isLoadingPlans}
                                          className="px-3 py-1.5 rounded-[3px] text-xs font-medium border border-gray-300 dark:border-white/15 text-gray-600 dark:text-white/60 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 transition-colors"
                                        >
                                          Unsubscribe
                                        </button>
                                      ) : (
                                        <button
                                          onClick={() => handleListeningPlanSubscribe(cp.planId)}
                                          disabled={isLoadingPlans}
                                          className="px-3 py-1.5 rounded-[3px] text-xs font-semibold bg-cerulean text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                        >
                                          Subscribe
                                        </button>
                                      )}
                                    </div>
                                  );
                                });
                              })()}

                              {/* Your current listening plan */}
                              {listeningPlans.length > 0 && (
                                <div className="pt-1 space-y-2">
                                  <div className="text-xs font-medium text-gray-900 dark:text-white">Your listening plan</div>
                                  {listeningPlans.map((plan) => {
                                    const planData = availablePlans.find(p => p.id === plan.planId);
                                    return (
                                      <div key={plan.id} className="rounded-[3px] border border-cerulean/30 bg-cerulean/5 p-3 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{planData?.name}</div>
                                          <div className="text-xs text-gray-500 dark:text-white/40 mt-0.5">
                                            {plan.listenedChapters?.length || 0} / {plan.totalChapters} chapters
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => handleListeningPlanUnsubscribe(plan.planId)}
                                          disabled={isLoadingPlans}
                                          className="flex-shrink-0 px-3 py-1.5 rounded-[3px] text-xs font-medium border border-gray-300 dark:border-white/15 text-gray-600 dark:text-white/60 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 transition-colors"
                                        >
                                          Unsubscribe
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </SettingsSection>
                          </>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Drawer for Badges & Streaks */}
      <Transition.Root show={badgesDrawerOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setBadgesDrawerOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 pl-10 max-w-md flex">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300 sm:duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="w-screen max-w-md">
                    <div className="h-full flex flex-col bg-white dark:bg-[#0d1520] shadow-xl py-6 px-6">
                      <div className="flex items-center justify-between mb-4">
                        <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                          <TrophyIcon className="w-5 h-5 text-cerulean" /> Badges &amp; Streaks
                        </Dialog.Title>
                        <button
                          className="text-gray-400 dark:text-white/35 hover:text-gray-600 dark:hover:text-white/70"
                          onClick={() => setBadgesDrawerOpen(false)}
                        >
                          <span className="sr-only">Close panel</span>
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <div className="flex-1 overflow-y-auto space-y-6">
                        {isLoadingBadges ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                            <span className="ml-3 text-gray-600 dark:text-white/60">Loading badges...</span>
                          </div>
                        ) : (
                          <>
                            {/* Current Streaks */}
                            <div>
                              <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <FireIcon className="w-5 h-5 text-coral" /> Current Streaks
                              </h3>
                              <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-[3px]">
                                  <div className="flex items-center space-x-3">
                                    <FireIcon className="w-6 h-6 text-orange-500" />
                                    <div>
                                      <div className="font-medium text-orange-900">Daily Recording</div>
                                      <div className="text-sm text-orange-700">
                                        {userStreaks.dailyRecording || 0} days
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-2xl font-bold text-orange-600">
                                    {userStreaks.dailyRecording || 0}
                                  </div>
                                </div>
                                
                                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-400/10 border border-blue-200 dark:border-blue-400/25 rounded-[3px]">
                                  <div className="flex items-center space-x-3">
                                    <SpeakerWaveIcon className="w-6 h-6 text-cerulean" />
                                    <div>
                                      <div className="font-medium text-blue-900 dark:text-blue-300">Listening Streak</div>
                                      <div className="text-sm text-blue-700 dark:text-blue-300/80">
                                        {userStreaks.listening || 0} days
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {userStreaks.listening || 0}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Earned Badges */}
                            <div>
                              <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <TrophyIcon className="w-5 h-5 text-cerulean" /> Earned Badges
                              </h3>
                              {userBadges.length === 0 ? (
                                <div className="text-center py-8">
                                  <TrophyIcon className="w-10 h-10 text-gray-300 dark:text-white/25 mx-auto mb-2" />
                                  <p className="text-gray-500 dark:text-white/40">No badges earned yet</p>
                                  <p className="text-sm text-gray-400 dark:text-white/35 mt-1">
                                    Start recording to earn your first badge!
                                  </p>
                                </div>
                              ) : (
                                <div className="grid grid-cols-2 gap-3">
                                  {userBadges.map((badge, index) => (
                                    <div key={index} className="p-3 bg-yellow-50 dark:bg-yellow-400/10 border border-yellow-200 dark:border-yellow-400/25 rounded-[3px] text-center">
                                      <div className="flex justify-center mb-1 text-yellow-600 dark:text-yellow-400">{getBadgeIcon(badge.type)}</div>
                                      <div className="text-sm font-medium text-yellow-900 dark:text-yellow-300">
                                        {getBadgeName(badge.type)}
                                      </div>
                                      <div className="text-xs text-yellow-700 dark:text-yellow-300/70 mt-1">
                                        {new Date(badge.earnedAt?.toDate?.() || badge.earnedAt).toLocaleDateString()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Available Badges */}
                            <div>
                              <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <StarIcon className="w-5 h-5 text-cerulean" /> Available Badges
                              </h3>
                              <div className="space-y-2">
                                {BADGE_DEFS.map((badge, index) => {
                                  const earned = userBadges.some(b => b.type === badge.type);
                                  return (
                                    <div key={index} className={`flex items-center space-x-3 p-2 rounded-[3px] ${
                                      earned ? 'bg-green-50 dark:bg-green-400/10 border border-green-200 dark:border-green-400/25' : 'bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10'
                                    }`}>
                                      <div className={earned ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-white/35'}>
                                        {getBadgeIcon(badge.type, 'w-6 h-6')}
                                      </div>
                                      <div className="flex-1">
                                        <div className={`text-sm font-medium ${earned ? 'text-green-900 dark:text-green-300' : 'text-gray-600 dark:text-white/60'}`}>
                                          {badge.name}
                                        </div>
                                        <div className={`text-xs ${earned ? 'text-green-700 dark:text-green-300/70' : 'text-gray-500 dark:text-white/40'}`}>
                                          {badge.description}
                                        </div>
                                      </div>
                                      {earned && <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default ProfilePage; 